import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Check, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { CTAButton } from "@/components/CTAButton";
import { Reveal, MaskedHeading, FadeIn } from "@/components/motion";
import { BUSINESS, img, PRODUCTS } from "@/lib/site";

// Shared blue-line section label used across the homepage sections.
export function SectionLabel({ children, className }) {
  return (
    <span className={cn("inline-flex items-center gap-3 font-mono-t text-[13px] font-medium uppercase tracking-[0.2em] text-brand", className)}>
      <span className="h-px w-8 bg-brand" />
      {children}
    </span>
  );
}

export function Section({ children, className, id, dark = false, testid }) {
  return (
    <section
      id={id}
      data-testid={testid}
      className={cn("relative", dark ? "bg-charcoal text-white" : "bg-white", className)}
    >
      {children}
    </section>
  );
}

export function Overline({ children, className, index }) {
  return (
    <span className={cn("overline inline-flex items-center gap-2", className)}>
      {index && <span className="text-charcoal-muted">{index}</span>}
      {children}
    </span>
  );
}

export function SectionHeading({ overline, index, title, subtitle, align = "left", light = false, className, as: Tag = "h2" }) {
  return (
    <div className={cn(align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-2xl", className)}>
      {overline && (
        <Reveal>
          <Overline index={index}>{overline}</Overline>
        </Reveal>
      )}
      {title && (
        <Reveal delay={0.05} className={cn(overline && "mt-5")}>
          <Tag
            className={cn(
              "font-heading text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl",
              light ? "text-white" : "text-charcoal"
            )}
          >
            {title}
          </Tag>
        </Reveal>
      )}
      {subtitle && (
        <Reveal delay={0.1} className="mt-5">
          <p className={cn("text-base leading-relaxed lg:text-lg", light ? "text-white/60" : "text-charcoal-muted")}>
            {subtitle}
          </p>
        </Reveal>
      )}
    </div>
  );
}

// Hero for interior (service / about) pages — big heading + spotlighted image.
export function PageHero({ overline, index, titleLines, text, ctaLabel, ctaTo, ctaHref, image, alt }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section data-testid="page-hero" className="border-b border-border bg-white">
      <div className="container-x grid gap-10 py-16 lg:grid-cols-12 lg:gap-14 lg:py-24">
        <div className="flex flex-col justify-center lg:col-span-6">
          <FadeIn variant="up">
            <Overline index={index}>{overline}</Overline>
          </FadeIn>
          <h1 className="mt-6 font-heading text-4xl font-medium leading-[1.02] tracking-tight text-charcoal sm:text-5xl lg:text-[4.25rem]">
            <MaskedHeading lines={titleLines} startDelay={0.12} />
          </h1>
          <FadeIn delay={0.45} variant="up" className="mt-7 max-w-xl">
            <p className="text-base leading-relaxed text-charcoal-muted lg:text-lg">{text}</p>
          </FadeIn>
          <FadeIn delay={0.58} variant="up" className="mt-9 flex flex-col gap-3 sm:flex-row">
            {(ctaTo || ctaHref) && (
              <CTAButton to={ctaTo} href={ctaHref} testid="hero-primary-cta">
                {ctaLabel}
              </CTAButton>
            )}
            <CTAButton href={`tel:${BUSINESS.phoneTel}`} variant="secondary" icon={Phone} testid="hero-call-cta">
              {BUSINESS.phoneDisplay}
            </CTAButton>
          </FadeIn>
        </div>

        <div ref={ref} className="relative lg:col-span-6">
          <div className="relative aspect-[4/3] overflow-hidden bg-surface lg:aspect-[5/6]">
            <motion.img
              src={img(image, 1200)}
              alt={alt}
              loading="eager"
              style={{ y }}
              className="absolute inset-0 h-[112%] w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export function Breadcrumb({ items }) {
  return (
    <nav className="container-x flex items-center gap-2 pt-4 font-mono-t text-[11px] uppercase tracking-[0.16em] text-charcoal-muted md:pt-6" aria-label="Breadcrumb">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-2">
          {i > 0 && <span className="text-border">/</span>}
          {it.to ? (
            <a href={it.to} className="transition-colors hover:text-brand">
              {it.label}
            </a>
          ) : (
            <span className="text-charcoal">{it.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

// Numbered process steps with technical grid feel.
export function ProcessSteps({ steps, columns = 4, compact = false, testid = "process-steps" }) {
  const colClass = { 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" }[columns];
  return (
    <div data-testid={testid} className={cn("grid grid-cols-1 border-t border-l border-border", colClass)}>
      {steps.map((s, i) => (
        <Reveal
          key={i}
          delay={i * 0.06}
          className={cn(
            "group relative flex flex-col border-b border-r border-border transition-colors",
            compact
              ? "p-6 lg:p-8 lg:hover:bg-brand/[0.03]"
              : "p-8 hover:bg-surface lg:p-10"
          )}
        >
          {compact && (
            <span className="absolute inset-x-0 top-0 h-0.5 bg-brand opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          )}
          <span className="font-mono-t text-sm text-brand">{String(i + 1).padStart(2, "0")}</span>
          <h3 className={cn("font-heading font-semibold text-charcoal", compact ? "mt-4 text-lg" : "mt-6 text-xl")}>
            {s.title}
          </h3>
          <p className={cn("leading-relaxed text-charcoal-muted", compact ? "mt-2 text-[14px]" : "mt-3 text-[15px]")}>
            {s.text}
          </p>
        </Reveal>
      ))}
    </div>
  );
}

// Bordered "engineering grid" of informational cells.
export function InfoGrid({ items, columns = 3, iconMap, hover = false, compact = false, testid = "info-grid" }) {
  const colClass = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-2 lg:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" }[columns];
  return (
    <div data-testid={testid} className={cn("grid grid-cols-1 border-t border-l border-border", colClass)}>
      {items.map((it, i) => {
        const Icon = it.icon && iconMap ? iconMap[it.icon] : null;
        return (
          <Reveal
            key={i}
            delay={(i % columns) * 0.05}
            className={cn(
              "group flex flex-col border-b border-r border-border",
              compact ? "p-7 lg:p-8 lg:transition-colors lg:duration-300 lg:hover:bg-brand/[0.03]" : "p-8 lg:p-9",
              hover && "lg:transition-colors lg:duration-300 lg:hover:bg-surface"
            )}
          >
            {Icon && (
              <span
                className={cn(
                  "grid h-11 w-11 place-items-center border border-border text-brand",
                  compact ? "mb-4" : "mb-6",
                  hover && "lg:transition-colors lg:duration-300 lg:group-hover:border-brand lg:group-hover:bg-brand lg:group-hover:text-white"
                )}
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
              </span>
            )}
            <h3 className="font-heading text-lg font-semibold text-charcoal">{it.title}</h3>
            <p className={cn("text-[15px] leading-relaxed text-charcoal-muted", compact ? "mt-2" : "mt-3")}>{it.text}</p>
          </Reveal>
        );
      })}
    </div>
  );
}

// Editorial asymmetric image gallery. Images easily swapped for real photos.
export function Gallery({ items, testid = "gallery" }) {
  // Desktop (lg+): first tile is a large 2x2 feature; the rest fill a 4-col grid.
  // Mobile/tablet (< lg): images stack vertically at their natural aspect ratio
  //   so the full real project photo — including before/after halves — is visible.
  const spanFor = (i) => (i === 0 ? "lg:col-span-2 lg:row-span-2" : "");
  return (
    <div
      data-testid={testid}
      className="flex flex-col gap-3 lg:grid lg:auto-rows-[240px] lg:grid-cols-4"
    >
      {items.map((it, i) => (
        <Reveal
          key={i}
          delay={(i % 4) * 0.06}
          className={cn("group relative overflow-hidden bg-surface", spanFor(i))}
        >
          <img
            src={img(it.src, 900)}
            alt={it.alt}
            loading="lazy"
            className={cn(
              "h-auto w-full object-contain transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] lg:h-full lg:group-hover:scale-[1.06]",
              it.fit === "contain" ? "lg:object-contain" : "lg:object-cover"
            )}
          />
        </Reveal>
      ))}
    </div>
  );
}

// Homepage compact product card — links to the matching Installation-page section.
function ProductCard({ product }) {
  return (
    <Reveal className="group h-full">
      <Link
        to={`/ac-installation-paphos#${product.id}`}
        data-testid={`product-card-${product.id}`}
        className="flex h-full flex-col overflow-hidden border border-border bg-white transition-all duration-300 hover:-translate-y-0.5 hover:border-charcoal/30 hover:shadow-[0_18px_40px_-24px_rgba(15,23,42,0.35)]"
      >
        <div className="aspect-[4/3] overflow-hidden bg-surface">
          <img
            src={product.image}
            alt={product.alt}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
          />
        </div>
        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-heading text-xl font-medium tracking-tight text-charcoal">{product.name}</h3>
          <p className="mt-2 text-sm leading-relaxed text-charcoal-muted">{product.blurb}</p>
          <ul className="mt-4 space-y-2">
            {product.features.map((f) => (
              <li key={f} className="flex items-center gap-2 text-[13px] text-charcoal">
                <Check className="h-3.5 w-3.5 shrink-0 text-brand" strokeWidth={2.5} />
                {f}
              </li>
            ))}
          </ul>
          <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand">
            View Models &amp; Prices
            <ArrowRight className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

// Homepage AC supply & installation block — text left, two product cards right.
export function BrandSection() {
  return (
    <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
      <div className="lg:col-span-5">
        <Reveal delay={0.05}>
          <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            AC Supply &amp; Installation
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-5 max-w-xl space-y-4">
          <p className="text-base leading-relaxed text-charcoal-muted lg:text-lg">
            NorthWork Cyprus supplies and installs selected AUX and GREE air-conditioning systems
            for homes and businesses across Paphos.
          </p>
          <p className="text-[15px] leading-relaxed text-charcoal-muted">
            Selected systems include a{" "}
            <span className="font-medium text-charcoal">5-year warranty</span> when supplied and
            installed by NorthWork Cyprus, subject to warranty terms and required annual servicing.
          </p>
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        <div className="grid gap-5 sm:grid-cols-2">
          {PRODUCTS.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Installation-page detailed product section with models & pricing + quote CTAs.
export function ProductDetailSection({ product, reverse = false }) {
  return (
    <div
      id={product.id}
      data-testid={`product-detail-${product.id}`}
      className="scroll-mt-28 grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-16"
    >
      <div className={cn("lg:col-span-6", reverse && "lg:order-2")}>
        <Reveal className="overflow-hidden border border-border bg-white">
          <div className="aspect-[4/3] overflow-hidden">
            <img src={product.image} alt={product.alt} loading="lazy" className="h-full w-full object-cover" />
          </div>
        </Reveal>
      </div>

      <div className={cn("lg:col-span-6", reverse && "lg:order-1")}>
        <Reveal delay={0.05}>
          <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
            {product.name}
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-4">
          <p className="text-base leading-relaxed text-charcoal-muted">{product.blurb}</p>
        </Reveal>

        <Reveal delay={0.14} className="mt-6 grid grid-cols-2 gap-x-8 gap-y-2.5">
          {product.features.map((f) => (
            <span key={f} className="flex items-center gap-2 text-[13px] text-charcoal">
              <Check className="h-3.5 w-3.5 shrink-0 text-brand" strokeWidth={2.5} />
              {f}
            </span>
          ))}
        </Reveal>

        <Reveal delay={0.18} className="mt-8 grid grid-cols-2 gap-3">
          {product.models.map((m) => (
            <div
              key={m.btu}
              data-testid={`model-${product.id}-${m.btu.replace(/[^0-9]/g, "")}`}
              className="rounded-xl border border-border bg-white p-4 transition-colors duration-300 hover:border-brand hover:bg-brand/[0.03]"
            >
              <span className="block font-heading text-base font-semibold tracking-tight text-charcoal">{m.btu}</span>
              <span className="mt-3 block font-mono-t text-[9px] uppercase tracking-[0.16em] text-charcoal-muted">
                Supplied &amp; Installed
              </span>
              <span className="mt-1 block font-heading text-2xl font-bold tracking-tight text-charcoal">
                {m.price}
              </span>
              <span className="mt-1 block font-mono-t text-[9px] uppercase tracking-[0.16em] text-charcoal-muted">
                VAT Included
              </span>
            </div>
          ))}
        </Reveal>

        <Reveal delay={0.2} className="mt-4">
          <p className="text-[13px] leading-relaxed text-charcoal-muted">
            Prices include VAT and basic installation. Any additional installation requirements or materials will be advised separately where applicable.
          </p>
        </Reveal>

        <Reveal delay={0.24} className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <CTAButton to="/contact" testid={`${product.id}-quote`} className="w-full sm:w-auto">
            Request Installation Quote
          </CTAButton>
          <CTAButton href={`tel:${BUSINESS.phoneTel}`} variant="secondary" icon={Phone} testid={`${product.id}-call`} className="w-full sm:w-auto">
            Call
          </CTAButton>
          <CTAButton href={BUSINESS.whatsappUrl} variant="secondary" icon={MessageCircle} testid={`${product.id}-whatsapp`} className="w-full sm:w-auto" target="_blank" rel="noopener noreferrer">
            WhatsApp
          </CTAButton>
        </Reveal>
      </div>
    </div>
  );
}

const INCLUDED_ITEMS = [
  "AC indoor and outdoor unit",
  "Professional installation",
  "Standard pipework and electrical connection within the basic installation allowance",
  "Vacuum procedure before commissioning",
  "System testing and commissioning",
  "Clean finishing of the installation",
  "5-year warranty on applicable supplied systems*",
];

// Shared "what the installation price includes" block + warranty note.
export function SuppliedIncludedSection() {
  return (
    <div data-testid="supplied-included">
      <Reveal delay={0.05}>
        <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl">
          Supplied &amp; Installed
        </h2>
      </Reveal>
      <div className="mt-6 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
        {INCLUDED_ITEMS.map((item, i) => (
          <Reveal key={item} delay={i * 0.04}>
            <div className="flex items-start gap-3 border-b border-border py-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" strokeWidth={2.5} />
              <span className="text-[15px] leading-snug text-charcoal">{item}</span>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1} className="mt-6 max-w-3xl">
        <p className="text-sm leading-relaxed text-charcoal-muted">
          Basic installation covers standard pipework and electrical connection within the included
          allowance. Any requirements outside the standard installation — such as extended pipework,
          additional brackets, difficult access, electrical alterations or removal of existing
          units — will be assessed and agreed with the customer before work proceeds.
        </p>
      </Reveal>
      <Reveal delay={0.14} className="mt-4 max-w-3xl border-l-2 border-brand/40 pl-4">
        <p className="font-mono-t text-[12px] leading-relaxed tracking-wide text-charcoal-muted">
          *5-year warranty applies to eligible AUX and GREE systems supplied and installed by
          NorthWork Cyprus and is subject to applicable warranty terms and required annual servicing.
        </p>
      </Reveal>
    </div>
  );
}

// Final CTA banner — dark, minimal, reused across pages.
export function CTABanner({ title, text, primaryLabel, primaryTo, image, overlayStyle, imageClass = "", paddingClass = "py-20 lg:py-28", showWhatsapp = false }) {
  return (
    <section data-testid="cta-banner" className="relative overflow-hidden bg-charcoal">
      {image && (
        <>
          <img
            src={img(image, 1600)}
            alt=""
            aria-hidden="true"
            className={`absolute inset-0 h-full w-full object-cover ${imageClass}`}
          />
          {overlayStyle ? (
            <div className="absolute inset-0" style={{ background: overlayStyle }} />
          ) : (
            <>
              {/* Light overall wash for polish + contrast */}
              <div className="absolute inset-0" style={{ background: "rgba(8,18,35,0.34)" }} />
              {/* Left shading so the heading/text stays clearly readable */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to right, rgba(8,18,35,0.62) 0%, rgba(8,18,35,0.32) 40%, rgba(8,18,35,0.08) 65%, rgba(8,18,35,0) 100%)",
                }}
              />
              {/* Stronger right-side shading, softly fading toward the centre */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(to left, rgba(8,18,35,0.72) 0%, rgba(8,18,35,0.55) 25%, rgba(8,18,35,0.28) 55%, rgba(8,18,35,0.08) 80%, rgba(8,18,35,0) 100%)",
                }}
              />
            </>
          )}
        </>
      )}
      <div className="grain absolute inset-0" />
      <div className={`container-x relative ${paddingClass}`}>
        <div className="max-w-2xl">
          <Reveal>
            <h2 className="font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
              {title}
            </h2>
          </Reveal>
          <Reveal delay={0.08} className="mt-6">
            <p className="text-base leading-relaxed text-white/65 lg:text-lg">{text}</p>
          </Reveal>
          <Reveal delay={0.16} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <CTAButton href={`tel:${BUSINESS.phoneTel}`} variant="primary" icon={Phone} testid="cta-banner-call">
              Call {BUSINESS.phoneDisplay}
            </CTAButton>
            {showWhatsapp && (
              <CTAButton
                href={BUSINESS.whatsappUrl}
                variant="outlineLight"
                icon={MessageCircle}
                testid="cta-banner-whatsapp"
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp
              </CTAButton>
            )}
            <CTAButton to={primaryTo || "/contact"} variant="outlineLight" testid="cta-banner-primary">
              {primaryLabel || "Contact NorthWork"}
            </CTAButton>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
